import { useState, useMemo, useEffect } from "react";

import { Form, message as antdMessage } from "antd";
import dayjs from "dayjs";
import { useParams, useNavigate } from "react-router-dom";

import { InvoiceCompanyHeaderInfo } from "../../../bitmaker/schemas/Billing/Invoice";

import { useCompanies } from "../../../hooks/useCompanies";
import { useInvoiceEmail } from "../../../hooks/useInvoiceEmail";
import { useInvoice } from "../../../hooks/useInvoice";
import { useInvoicePDF } from "../../../hooks/useInvoicePDF";
import { useSaveInvoice } from "../../../hooks/useSaveInvoice";
import { useNextInvoiceNumber } from "../../../hooks/useNextInvoiceNumber";
import { useClients } from "../../../hooks/useClients";

import { InvoiceConfirmBackModal } from "./InvoiceConfirmBackModal";
import { InvoiceManagerContent } from "./InvoiceManagerContent";
import { InvoiceManagerHeader } from "./InvoiceManagerHeader";
import InvoicePayments from "./InvoicePayments";
import { InvoiceSendEmail } from "./InvoiceSendEmail";

import { mapCompanyInfoToHeader } from "../../../utils";

type Mode = "create" | "view" | "edit";

const removeAmountDue = ({ amountDue, ...rest }: any) => rest;

const normalizeDate = (d: any) => {
  if (!d) return null;
  if (dayjs.isDayjs(d)) return d.toISOString();
  return d;
};

const normalizeFormValues = (values: any) => {
  const normalizeOptional = (v: any) =>
    typeof v === "string" && v.trim() === "" ? null : v;

  return {
    ...values,
    issueDate: normalizeDate(values.issueDate),
    dueDate: normalizeDate(values.dueDate),
    reference: normalizeOptional(values.reference),
    notes: normalizeOptional(values.notes),
    terms: normalizeOptional(values.terms),
    items: (values.items || []).map((item: any) => ({
      ...item,
      itemName: item.itemName?.trim(),
      itemDescription: normalizeOptional(item.itemDescription),
    })),
  };
};

export default function InvoiceManager() {
  const navigate = useNavigate();

  const [form] = Form.useForm();

  const watchLanguage = Form.useWatch("language", form);
  const watchCurrency = Form.useWatch("currency", form);

  const { invoiceId } = useParams<{ invoiceId: string }>();

  // Create/View/Edit mode management
  const [mode, setMode] = useState<Mode>(invoiceId ? "view" : "create");
  const isExistingInvoice = mode === "view" || mode === "edit";

  // Fetch clients for the client selector
  const { clients, loadingClients, fetchClientsError } = useClients();

  // Company Info for header (new invoices)
  const { companies, loadingCompanies, fetchCompaniesError } = useCompanies();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null,
  );
  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === selectedCompanyId) || null,
    [companies, selectedCompanyId],
  );

  useEffect(() => {
    if (
      mode === "create" &&
      companies.length > 0 &&
      selectedCompanyId === null
    ) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [companies, mode, selectedCompanyId]);

  // Invoice data for view/edit existing invoices
  const { invoice, loadingInvoice, invoiceFetchError, refetchInvoice } =
    useInvoice(invoiceId);

  // Download PDF functionality
  const { downloadInvoicePDF } = useInvoicePDF(invoice);

  // Send invoice by email functionality
  const {
    emailForm,
    isSendInvoiceModalOpen,
    isSendingInvoice,
    sendInvoiceError,
    handleOpenSendInvoiceModal,
    handleCloseSendInvoiceModal,
    handleSendEmail,
    validateEmail,
  } = useInvoiceEmail(invoice);

  // Unsaved changes management
  const [isConfirmBackOpen, setIsConfirmBackOpen] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState<any>(null);

  useEffect(() => {
    if (invoice && (mode === "view" || mode === "edit")) {
      form.setFieldsValue(invoice);

      setTimeout(() => {
        const values = form.getFieldsValue(true);

        setInitialFormValues(normalizeFormValues(removeAmountDue(values)));
      }, 0);
    }
  }, [invoice, mode, form]);

  const hasUnsavedChanges = () => {
    if (!initialFormValues) return false;

    const currentValues = normalizeFormValues(
      removeAmountDue(form.getFieldsValue(true)),
    );

    return JSON.stringify(currentValues) !== JSON.stringify(initialFormValues);
  };

  // Determine header display info based on mode and data availability
  const headerDisplayInfo: InvoiceCompanyHeaderInfo | null = useMemo(() => {
    // Existing invoice
    if (isExistingInvoice) {
      if (!invoice) return null;

      return {
        companyCorporateName: invoice.companyCorporateName,
        companyName: invoice.companyName,
        companyEmailAddress: invoice.companyEmailAddress,
        companyPhoneNumber: invoice.companyPhoneNumber,
        companyAddressLine: invoice.companyAddressLine,
        companyTownCity: invoice.companyTownCity,
        companyStateProvince: invoice.companyStateProvince,
        companyPostalCode: invoice.companyPostalCode,
        companyCountry: invoice.companyCountry,
        companyTaxName: invoice.companyTaxName,
        companyTaxNumber: invoice.companyTaxNumber,
      };
    }

    // New invoice
    if (!selectedCompany) return null;

    return mapCompanyInfoToHeader(selectedCompany);
  }, [isExistingInvoice, invoice, selectedCompany]);

  // Save invoice functionality
  const { saveInvoice, isSavingInvoice, saveInvoiceError } =
    useSaveInvoice(invoiceId);

  const handleSaveInvoice = async () => {
    try {
      const values = await form.validateFields();

      if (!headerDisplayInfo) {
        antdMessage.error("Missing company information.");
        return;
      }

      const savedInvoice = await saveInvoice(values, headerDisplayInfo);

      if (!savedInvoice) {
        antdMessage.error(
          saveInvoiceError || "Failed to save invoice. Please try again.",
        );
        return;
      }

      antdMessage.success(
        `Invoice #${savedInvoice.invoiceNumber} ${isExistingInvoice ? "updated" : "created"
        } successfully!`,
      );

      if (!isExistingInvoice) {
        navigate("/billing/invoices");
      } else {
        setMode("view");
        refetchInvoice();
      }
    } catch (error: any) {
      if (error.errorFields) {
        console.log("Validation Failed:", error);
        antdMessage.error("Please fix the highlighted fields before saving.");
      } else {
        console.error("Save Invoice Error:", error);
        antdMessage.error(
          "An unexpected error occurred while saving the invoice.",
        );
      }
    }
  };

  // Next invoice number functionality for new invoices
  const {
    nextInvoiceNumber,
    loadingNextInvoiceNumber,
    fetchNextInvoiceNumberError,
  } = useNextInvoiceNumber(invoiceId);

  useEffect(() => {
    if (mode === "create" && nextInvoiceNumber != null) {
      form.setFieldsValue({ invoiceNumber: nextInvoiceNumber });
    }
  }, [mode, nextInvoiceNumber, form]);

  // Invoice actions
  const handleEditInvoice = () => {
    setMode("edit");
  };

  const handleDeleteInvoice = () => {
    antdMessage.warning(`Delete functionality not yet implemented.`);
  };

  const handleBack = () => {
    if (mode === "edit") {
      if (hasUnsavedChanges()) {
        setIsConfirmBackOpen(true);
        return;
      }

      setMode("view");
      refetchInvoice();
      return;
    }

    navigate("/billing/invoices");
  };

  // Default values for new invoice
  const initialValues = useMemo(
    () => ({
      language: "en",
      currency: "USD",
      issueDate: dayjs(),
      dueDate: dayjs().add(10, "day"),
      invoiceNumber: undefined,
      items: [
        {
          itemName: "",
          itemDescription: null,
          quantity: 1,
          appliedTaxes: [],
        },
      ],
      amountPaid: 0,
    }),
    [],
  );

  useEffect(() => {
    if (mode === "create") {
      form.resetFields();
      form.setFieldsValue(initialValues);
    }
  }, [mode, form, initialValues]);

  useEffect(() => {
    if (mode === "create" && selectedCompanyId != null) {
      form.setFieldsValue({ companyId: selectedCompanyId });
    }
  }, [mode, selectedCompanyId, form]);

  const canCreateInvoice =
    !loadingCompanies &&
    companies.length > 0 &&
    !loadingClients &&
    clients.length > 0 &&
    !loadingNextInvoiceNumber &&
    nextInvoiceNumber != null &&
    !fetchClientsError &&
    !fetchCompaniesError &&
    !fetchNextInvoiceNumberError;

  // Invoice company for email
  const invoiceCompany = useMemo(() => {
    if (!invoice) return null;
    return companies.find((c) => c.id === invoice.companyId) || null;
  }, [invoice, companies]);

  return (
    <>
      <div className="w-full h-full flex flex-col bg-gray-50">
        {/* Header Bar */}
        <InvoiceManagerHeader
          invoice={{
            invoiceNumber: invoice?.invoiceNumber,
            hasError: !!invoiceFetchError,
            isLoading: loadingInvoice,
          }}
          mode={mode}
          company={{
            companies,
            selectedCompanyId,
            loadingCompanies,
            onChangeCompany: setSelectedCompanyId,
          }}
          settings={{
            currency: watchCurrency,
            language: watchLanguage,
            onChangeLanguage: (lang) => form.setFieldsValue({ language: lang }),
            onChangeCurrency: (curr) => form.setFieldsValue({ currency: curr }),
          }}
          state={{ isSavingInvoice, canCreateInvoice }}
          actions={{
            onBack: handleBack,
            onEdit: handleEditInvoice,
            onSave: handleSaveInvoice,
            onSendEmail: handleOpenSendInvoiceModal,
            onDownloadPDF: downloadInvoicePDF,
            onDelete: handleDeleteInvoice,
          }}
        />

        {/* Content Area */}
        <InvoiceManagerContent
          mode={mode}
          form={form}
          invoiceState={{ invoice, invoiceFetchError, loadingInvoice }}
          nextInvoiceNumberState={{
            loadingNextInvoiceNumber,
            fetchNextInvoiceNumberError,
          }}
          companyState={{
            headerDisplayInfo,
            hasError: !!fetchCompaniesError,
            loadingCompanies,
          }}
          selectedCompany={selectedCompany}
          sidebarData={{
            clients,
            loadingClients,
            fetchClientsError,
          }}
        />

        {/* Invoice Payments */}
        {mode === "view" && invoice && (
          <div className="border-t bg-white shadow-sm">
            <div className="max-w-4xl mx-auto">
              <InvoicePayments
                invoice={invoice}
                onInvoiceUpdate={refetchInvoice}
              />
            </div>
          </div>
        )}
      </div>

      {/* Send Email Modal */}
      {invoice && (
        <InvoiceSendEmail
          open={isSendInvoiceModalOpen}
          onClose={handleCloseSendInvoiceModal}
          onSubmit={handleSendEmail}
          form={emailForm}
          isSending={isSendingInvoice}
          error={sendInvoiceError}
          validateEmail={validateEmail}
          invoiceNumber={invoice.invoiceNumber}
          senderEmail={invoiceCompany?.billingEmailAddress || null}
        />
      )}

      {/* Confirm Back Modal */}
      <InvoiceConfirmBackModal
        open={isConfirmBackOpen}
        onCancel={() => setIsConfirmBackOpen(false)}
        onConfirm={() => {
          setIsConfirmBackOpen(false);
          setMode("view");
          refetchInvoice();
        }}
      />
    </>
  );
}
